import {customElement, property, state} from 'lit/decorators.js';
import {css, CSSResult, html, LitElement, nothing, PropertyValues, TemplateResult, unsafeCSS,} from 'lit';
import arrowRight from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-16_chevron-right.svg?element';
import arrowLeft from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-16_chevron-left.svg?element';
import Swiper from 'swiper';
import {classMap} from 'lit/directives/class-map.js';
import {SwiperOptions} from 'swiper/types';
import {SwiperContainer} from 'swiper/element';
import {consume} from '@lit/context';
import styles from './swiper.scss?inline';
import '../wishlist-image/wishlist-image.js';
import {Image, Messages, Product} from '../../../@types/wishlist.js';
import {previewAnimationContext, PreviewState} from '../../../scripts/context/previewAnimationContext.js';
import {mediaQuery} from '../../../scripts/service/media-query.js';
import {i18nContext} from '../../../scripts/context/i18nContext.js';
import {WishlistAppConfig, wishlistAppConfigContext} from '../../../scripts/context/appConfigContext.js';
import {hideIconFromAssistiveTechnology} from '../icon/icon-accessibility.js';

@customElement('ents-swiper')
export class EntsSwiper extends LitElement {
  @property({type: Array}) images: Image[] = [];

  @property({type: Number, attribute: 'initial-image-index'}) initialImageIndex = 0;

  @property({type: Number}) position = 0;

  @property({type: Boolean, attribute: 'is-faded'}) isFaded: boolean = false;

  @property({type: String, attribute: 'alt-text'}) altText: string = '';

  @property({type: Object}) product: Product | undefined = undefined;

  @property({type: Boolean, attribute: 'in-viewport'}) inViewport: boolean = false;

  @state() swiper?: Swiper;

  @state() showSwiper: boolean = false;

  @state() imagesLoaded: boolean[] = [];

  @consume({context: previewAnimationContext, subscribe: true})
  private previewAnimationContext!: PreviewState;

  @consume({context: i18nContext})
  private messages!: Messages;

  @consume({context: wishlistAppConfigContext})
  private wishlistAppConfig!: WishlistAppConfig;

  private animationTimeout?: number;

  private waitBetweenTransitions = 150;

  private animationStepDuration = 500;

  private swiperThreshold: number = 2;

  private swiperInitialized = false;

  static get styles(): CSSResult {
    return css`${unsafeCSS(styles)}`;
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.destroySwiper();
  }

  private initializeSwiper() {
    if (this.swiperInitialized) return;

    this.showSwiper = false;
    const swiperEl: SwiperContainer | null | undefined = this.shadowRoot?.querySelector('swiper-container');

    let animationSpeedInMs = 300;
    if (mediaQuery.prefersReducedMotion()) {
      /* https://github.com/nolimits4web/Swiper/issues/1702 */
      animationSpeedInMs = 1;
    }

    if (swiperEl) {
      this.swiperInitialized = true;

      const swiperParams: SwiperOptions = {
        speed: animationSpeedInMs,
        initialSlide: this.initialImageIndex,
        autoplay: false,
        loop: true,
        slidesPerView: 1,
        spaceBetween: 6,
        runCallbacksOnInit: false,
        lazyPreloadPrevNext: 1,
        on: {
          afterInit: (swiper: Swiper) => {
            this.swiper = swiper;
            this.showSwiper = true;

            if (this.position === 1 && ('ontouchstart' in window || navigator.maxTouchPoints > 0)) {
              if (!mediaQuery.prefersReducedMotion()) {
                this.runPreviewAnimation();
              }
            }
          },
        },
      };

      Object.assign(swiperEl, swiperParams);
      swiperEl.initialize();
    }
  }

  private handleInitialImageLoad(index: number) {
    if (this.imagesLoaded.length !== this.images.length) {
      this.imagesLoaded = new Array(this.images.length).fill(false);
    }
    this.handleImageLoad(index);
    this.tryInitializeSwiper();
  }

  private handleImageLoad(index: number) {
    if (!this.imagesLoaded[index]) {
      this.imagesLoaded = [
        ...this.imagesLoaded.slice(0, index),
        true,
        ...this.imagesLoaded.slice(index + 1),
      ];
    }
  }

  private handleSwiperImageLoad(index: number) {
    this.handleImageLoad(index);
    this.showSwiper = true;
  }

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    if (changedProperties.has('images') || changedProperties.has('product')) {
      this.imagesLoaded = new Array(this.images.length).fill(false);
      this.destroySwiper();
      this.tryInitializeSwiper();
    }

    if (changedProperties.has('inViewport') || changedProperties.has('initialImageIndex')) {
      if (this.inViewport) {
        this.tryInitializeSwiper();
      } else {
        this.destroySwiper();
      }
    }

    // slide to correct initial index if user clicked model switch
    if (changedProperties.get('initialImageIndex') != null && this.swiper != null) {
      if (this.previewAnimationContext.previewAnimationStarted && !this.previewAnimationContext.previewAnimationEnded) {
        this.cancelPreviewAnimation(() => this.swiper?.slideToLoop(this.initialImageIndex));
      } else {
        this.swiper.slideToLoop(this.initialImageIndex);
      }
    }
  }

  private destroySwiper() {
    if (!this.swiperInitialized) return;

    this.cancelPreviewAnimation();
    this.swiper?.destroy(true, true);
    this.swiper = undefined;
    this.swiperInitialized = false;
    this.showSwiper = false;
  }

  private tryInitializeSwiper() {
    if (this.swiperInitialized || !this.imagesLoaded[this.initialImageIndex] || !this.inViewport) {
      return;
    }
    this.initializeSwiper();
  }

  protected render(): TemplateResult {
    const initialImageLoading = this.inViewport ? 'eager' : 'lazy';
    return html`
      <div class="swiper-wrapper${classMap({faded: this.isFaded})}" data-test-ents-wishlist-swiper-wrapper>
        ${this.showSwiper ? nothing : html`
          <div class="ents-image-container">${this.renderImage(this.initialImageIndex, false, initialImageLoading)}</div>
        `}
        ${this.imagesLoaded[this.initialImageIndex] ? nothing : html`
          <div class="ents-image-skeleton"></div>
        `}
        ${this.images.length >= this.swiperThreshold && this.inViewport ? html`
          <div class="swiper-button-prev" @click="${this.handlePrevButtonClick}" data-test-ents-wishlist-swiper-prev>
            ${hideIconFromAssistiveTechnology(arrowLeft())}
            <span class="sr-only">${this.messages['ents_wishlist_images_slider_previous']}</span>
          </div>
          <div class="swiper-button-next" @click="${this.handleNextButtonClick}" data-test-ents-wishlist-swiper-next>
            ${hideIconFromAssistiveTechnology(arrowRight())}
            <span class="sr-only">${this.messages['ents_wishlist_images_slider_next']}</span>
          </div>
          <swiper-container
              id="ents-swiper-${this.position}"
              init="false"
              class="${classMap({hidden: !this.showSwiper})}">
            ${[...this.images.keys()].map((index) => {
              const loading = index === this.initialImageIndex && this.inViewport ? 'eager' : 'lazy';
              return html`
              <swiper-slide>
                ${this.renderImage(index, true, loading)}
                ${this.imagesLoaded[index] ? nothing : html`
                  <div class="swiper-lazy-preloader ents-image-skeleton"></div>
                `}
              </swiper-slide>`;
            })}
          </swiper-container>
        ` : nothing}
      </div>
    `;
  }

  protected renderImage(index: number, inSwiper: boolean, loading: 'lazy' | 'eager' = 'lazy'): TemplateResult {
    const sizes = this.wishlistAppConfig.productTileRedesign
      ? '(min-width: 1920px) 214px, (min-width: 1160px) 16vw, (min-width: 660px) 25vw, 50vw'
      : '(min-width: 1920px) 342px, (min-width: 1660px) 20vw, (min-width: 1160px) 25vw, (min-width: 760px) 33vw, (min-width: 560px) 50vw, 153px';
    return html`
      <ents-wishlist-image
        data-test-ents-wishlist-picture="${inSwiper ? 'swiper' : 'static'}"
        image-url="${this.images[index].url}"
        sizes="${sizes}"
        alt-text="${this.altText}"
        data-index="${index}"
        loading="${loading}"
        ?in-swiper="${inSwiper}"
        ?is-beauty="${this.product?.isBeauty === true}"
        brand-code="${this.product?.brandCode}"
        .onImageLoad="${() => (inSwiper ? this.handleSwiperImageLoad(index) : this.handleInitialImageLoad(index))}"
      ></ents-wishlist-image>
    `;
  }

  private handlePrevButtonClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.swiper?.slidePrev();
  }

  private handleNextButtonClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.swiper?.slideNext();
  }

  private runPreviewAnimation() {
    if (
      !this.swiper
      || this.swiper.destroyed
      || this.previewAnimationContext.previewAnimationStarted
      || this.previewAnimationContext.previewAnimationEnded
    ) return;

    this.previewAnimationContext.startPreviewAnimation();
    this.swiper.disable();

    const halfSlideWidth = this.swiper.width / 2;
    const currentTranslate = this.swiper.translate;
    const animationTranslateSteps = [currentTranslate - halfSlideWidth, currentTranslate];
    let stepIndex = 0;

    const performAnimationStep = () => {
      if (!this.swiper || this.swiper.destroyed || this.previewAnimationContext.previewAnimationEnded) return;

      if (stepIndex >= animationTranslateSteps.length) {
        this.swiper.enable();
        this.previewAnimationContext.endPreviewAnimation();
        return;
      }

      const translate = animationTranslateSteps[stepIndex];
      this.swiper.translateTo(translate, this.animationStepDuration, false);

      stepIndex += 1;
      this.animationTimeout = window.setTimeout(performAnimationStep, this.animationStepDuration + this.waitBetweenTransitions);
    };

    this.animationTimeout = window.setTimeout(performAnimationStep, this.animationStepDuration);
  }

  private cancelPreviewAnimation(callback?: () => void) {
    if (this.position !== 1) return;

    if (!this.previewAnimationContext.previewAnimationStarted || this.previewAnimationContext.previewAnimationEnded) {
      if (this.swiper && !this.swiper.destroyed && callback) {
        callback();
      }
      return;
    }

    this.previewAnimationContext.endPreviewAnimation();
    window.clearTimeout(this.animationTimeout);

    if (this.swiper && !this.swiper.destroyed) {
      this.swiper.enable();
      if (callback) {
        this.animationTimeout = window.setTimeout(callback, this.animationStepDuration + this.waitBetweenTransitions);
      } else {
        this.animationTimeout = window.setTimeout(
          () => this.swiper?.slideToClosest(),
          this.animationStepDuration + this.waitBetweenTransitions,
        );
      }
    }
  }
}
